import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/supabaseClient";

export interface DatabaseCategory {
  id: string;
  user_id: string;
  name: string;
  color?: string;
  created_at: string;
  updated_at: string;
}

export const useCategories = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["categories", user?.id];

  const {
    data: categories = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("user_id", user.id)
        .order("name");

      if (error) throw error;
      return data as DatabaseCategory[];
    },
    enabled: !!user,
  });

  const createCategory = useMutation({
    mutationKey: ["createCategory"],
    mutationFn: async (
      category: Omit<
        DatabaseCategory,
        "id" | "user_id" | "created_at" | "updated_at"
      >,
    ) => {
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("categories")
        .insert({
          ...category,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async (newItem) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<DatabaseCategory[]>(queryKey);
      queryClient.setQueryData<DatabaseCategory[]>(queryKey, (old = []) => [
        ...old,
        {
          ...newItem,
          id: crypto.randomUUID(),
          user_id: user?.id ?? "",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as DatabaseCategory,
      ]);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateCategory = useMutation({
    mutationKey: ["updateCategory"],
    mutationFn: async ({
      id,
      ...updates
    }: Partial<DatabaseCategory> & { id: string }) => {
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("categories")
        .update(updates)
        .eq("id", id)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async ({ id, ...updates }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<DatabaseCategory[]>(queryKey);
      queryClient.setQueryData<DatabaseCategory[]>(queryKey, (old = []) =>
        old.map((cat) =>
          cat.id === id
            ? { ...cat, ...updates, updated_at: new Date().toISOString() }
            : cat,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteCategory = useMutation({
    mutationKey: ["deleteCategory"],
    mutationFn: async (id: string) => {
      if (!user) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("categories")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) throw error;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<DatabaseCategory[]>(queryKey);
      queryClient.setQueryData<DatabaseCategory[]>(queryKey, (old = []) =>
        old.filter((cat) => cat.id !== id),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    categories,
    isLoading,
    error,
    createCategory,
    updateCategory,
    deleteCategory,
  };
};
